/**
 * AI Recommendations Page
 * AI-powered insights and suggestions
 */
import React, { useState } from 'react';
import { 
  Brain, 
  Zap, 
  CheckCircle2, 
  Clock,
  AlertTriangle,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Shield,
  Target
} from 'lucide-react';

const AI_RECOMMENDATIONS = [
  {
    id: 1,
    title: 'Optimize Scope 3 Category 11 Methodology',
    description: 'Based on your product portfolio, switching to lifecycle assessment methodology could improve data accuracy by 23%.',
    impact: 'high',
    effort: 'medium',
    category: 'data-quality',
    status: 'new',
    potentialImprovement: '+23% accuracy',
  },
  {
    id: 2,
    title: 'Supplier Engagement Gap Identified',
    description: '15 high-risk suppliers have not responded to ESG surveys in 90+ days. Recommend prioritized follow-up.',
    impact: 'high',
    effort: 'low',
    category: 'risk',
    status: 'urgent',
    potentialImprovement: '15% risk reduction',
  },
  {
    id: 3,
    title: 'Cross-Framework Evidence Reuse Opportunity',
    description: 'Your CSRD E1-6 evidence can automatically satisfy 3 CDP questions. Enable auto-mapping to save 4 hours.',
    impact: 'medium',
    effort: 'low',
    category: 'efficiency',
    status: 'new',
    potentialImprovement: '4h time saved',
  },
  {
    id: 4,
    title: 'Upcoming ESRS Disclosure Pattern',
    description: 'Companies in your sector typically include biodiversity metrics. Consider adding ESRS E4 to your cycle.',
    impact: 'medium',
    effort: 'high',
    category: 'compliance',
    status: 'suggestion',
    potentialImprovement: 'Better peer alignment',
  },
];

const IMPACT_CONFIG = {
  high: { label: 'High Impact', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  medium: { label: 'Medium Impact', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  low: { label: 'Low Impact', bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]' },
};

const STATUS_CONFIG = {
  new: { label: 'New', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  urgent: { label: 'Urgent', bg: 'bg-red-500/20', text: 'text-red-400' },
  suggestion: { label: 'Suggestion', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  implemented: { label: 'Implemented', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
};

const CATEGORY_ICONS = {
  'data-quality': TrendingUp,
  'risk': AlertTriangle,
  'efficiency': Zap,
  'compliance': Shield,
};

export default function AIRecommendationsPage() {
  const [filterCategory, setFilterCategory] = useState('all');

  const filteredRecommendations = AI_RECOMMENDATIONS.filter(rec => {
    return filterCategory === 'all' || rec.category === filterCategory;
  });

  const urgentCount = AI_RECOMMENDATIONS.filter(r => r.status === 'urgent').length;
  const highImpactCount = AI_RECOMMENDATIONS.filter(r => r.impact === 'high').length;

  return (
    <div className="space-y-6" data-testid="ai-recommendations-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold text-white">AI Recommendations</h1>
            <span className="px-2 py-0.5 text-[10px] font-bold bg-purple-500/20 text-purple-400 rounded flex items-center gap-1">
              <Sparkles size={10} />
              AI
            </span>
          </div>
          <p className="text-sm text-[#6B6B6B]">
            Intelligent suggestions to improve your compliance posture and efficiency
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Brain size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{AI_RECOMMENDATIONS.length}</div>
              <div className="text-xs text-[#6B6B6B]">Recommendations</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertTriangle size={18} className="text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-400">{urgentCount}</div>
              <div className="text-xs text-[#6B6B6B]">Urgent</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Target size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-400">{highImpactCount}</div>
              <div className="text-xs text-[#6B6B6B]">High Impact</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Zap size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">~12h</div>
              <div className="text-xs text-[#6B6B6B]">Potential Time Saved</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        {['all', 'data-quality', 'risk', 'efficiency', 'compliance'].map(category => (
          <button
            key={category}
            onClick={() => setFilterCategory(category)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
              filterCategory === category 
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
            }`}
          >
            {category === 'all' ? 'All' : category.replace('-', ' ')}
          </button>
        ))}
      </div>

      {/* Recommendations */}
      <div className="space-y-4">
        {filteredRecommendations.map(rec => {
          const impactConfig = IMPACT_CONFIG[rec.impact];
          const statusConfig = STATUS_CONFIG[rec.status];
          const CategoryIcon = CATEGORY_ICONS[rec.category] || Brain;
          
          return (
            <div 
              key={rec.id} 
              className={`bg-[#111111] border ${rec.status === 'urgent' ? 'border-red-500/30' : 'border-[#1F1F1F]'} rounded-xl p-5 hover:bg-[#151515] transition-all`}
              data-testid={`recommendation-${rec.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-purple-500/20">
                  <CategoryIcon size={20} className="text-purple-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="text-base font-semibold text-white">{rec.title}</h3>
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${statusConfig.bg} ${statusConfig.text}`}>
                      {statusConfig.label}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${impactConfig.bg} ${impactConfig.text}`}>
                      {impactConfig.label}
                    </span>
                  </div>
                  <p className="text-sm text-[#A0A0A0] mb-3">{rec.description}</p>
                  <div className="flex items-center gap-4 text-xs text-[#6B6B6B]">
                    <span>Effort: <span className="capitalize">{rec.effort}</span></span>
                    <span className="text-emerald-400">{rec.potentialImprovement}</span>
                  </div>
                </div>
                <button className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors flex items-center gap-1">
                  Apply <ArrowRight size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredRecommendations.length === 0 && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <Brain size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No recommendations</h3>
          <p className="text-sm text-[#6B6B6B]">All caught up! Check back later for new AI insights.</p>
        </div>
      )}
    </div>
  );
}
